import { t } from "@web-monorepo/i18n/macro";
import { getImageSrc } from "@web-monorepo/static-assets";
import MaterialIcon from "src/shared/components/MaterialIcon";
import Audio from "./Audio";
import styles from "./RadioDisplay.module.css";
import { useRadioDisplay, type UseRadioDisplayProps, type UseRadioDisplayResult } from "./useRadioDisplay.vm";

const radio = getImageSrc("toolkit/src/pods/radio/assets/blue-radio.png");

type RadioDisplayProps = UseRadioDisplayProps & {
  /**
   * Dependency injection for the view model — Storybook stories override this
   * with a stub `useVm` returning canned state. Production callers leave the
   * default.
   */
  di?: {
    useVm?: (props: UseRadioDisplayProps) => UseRadioDisplayResult;
  };
};

function PlaybackCaption({ playing, caption }: { playing?: boolean; caption: string }) {
  if (!playing) return null;
  return (
    <p aria-live="assertive" className={styles.caption}>
      {caption}
    </p>
  );
}

export default function RadioDisplay({ di, ...props }: RadioDisplayProps) {
  const useVm = di?.useVm ?? useRadioDisplay;
  const {
    channel,
    currentTrackIndex,
    playing,
    hasAudioSrc,
    audioSrc,
    caption,
    togglePlayback,
    nextTrack,
    previousTrack,
    onAudioPlaying,
    onAudioPause,
    onAudioEnded,
  } = useVm(props);

  return (
    <section>
      <button
        type="button"
        aria-label={t`Toggle playback`}
        data-name="radio_display:toggle_playback"
        className={styles.radioImageContainer}
        onClick={togglePlayback}
      >
        <img src={radio} alt="" />
        <PlaybackCaption playing={playing} caption={caption} />
      </button>

      <div className={styles.playbackControl}>
        <button
          type="button"
          data-name="radio_display:skip_previous"
          className={styles.buttonReset}
          onClick={previousTrack}
          aria-label={t`Previous`}
        >
          <MaterialIcon icon="skip_previous" className={styles.sideButton} />
        </button>
        {playing ? (
          <button
            type="button"
            data-name="radio_display:pause_button"
            className={styles.buttonReset}
            onClick={togglePlayback}
            aria-label={t`Pause`}
          >
            <MaterialIcon icon="pause" className={styles.mainButton} />
          </button>
        ) : (
          <button
            type="button"
            data-name="radio_display:play_button"
            className={styles.buttonReset}
            onClick={togglePlayback}
            aria-label={t`Play`}
          >
            <MaterialIcon icon="play_arrow" className={styles.mainButton} />
          </button>
        )}
        <button
          type="button"
          data-name="radio_display:skip_next"
          className={styles.buttonReset}
          onClick={nextTrack}
          aria-label={t`Next`}
        >
          <MaterialIcon icon="skip_next" className={styles.sideButton} />
        </button>
      </div>
      {hasAudioSrc && audioSrc && channel && currentTrackIndex !== undefined && (
        <Audio
          src={audioSrc}
          isPlaying={!!playing}
          onPlaying={onAudioPlaying}
          onPause={onAudioPause}
          onEnded={onAudioEnded}
          caption={caption}
        />
      )}
    </section>
  );
}
